import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import VisibilityIcon from '@mui/icons-material/Visibility'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import DownloadIcon from '@mui/icons-material/Download'
import NotificationsIcon from '@mui/icons-material/Notifications'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RestoreIcon from '@mui/icons-material/Restore'
import CancelIcon from '@mui/icons-material/Cancel'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { Store as ShipperIcon, LocalShipping as FreighterIcon } from '@mui/icons-material'
import {
  analyticsService,
  subscriptionPlanService,
  subscriptionService,
  accountService,
  AccountDto,
  AccountType,
  SubscriptionDto,
  SubscriptionInvoiceDto,
  SubscriptionPlanDto,
  SubscriptionStatus,
} from '../api/adminServices'
import { formatCurrency } from '../utils/formatCurrency'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import { exportCsv } from '../utils/exportCsv'
import ConfirmDialog from '../components/ConfirmDialog'
import AccountSubscriptionActions from '../components/AccountSubscriptionActions'
import { useToast } from '../components/useToast'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'
import { PropertyView, PropertySection, PropertyField } from '../components/PropertyView'
import { fmtDate } from '../utils/propertyFormat'
import { freighterColor, shipperColor } from '../theme/theme'

type TabKey = 'subscriptions' | 'overdue' | 'analytics'
type AccountTypeFilter = 'ALL' | AccountType

const PAGE_SIZE = 20

const STATUS_OPTIONS: SubscriptionStatus[] = [
  'PENDING',
  'TRIAL',
  'ACTIVE',
  'PAST_DUE',
  'CANCELLED',
  'EXPIRED',
  'SUSPENDED',
]

const REMINDER_TYPES = [
  { value: 'TRIAL_ENDING', label: 'Trial ending soon' },
  { value: 'RENEWAL', label: 'Upcoming renewal' },
  { value: 'PAYMENT_FAILED', label: 'Payment failed' },
  { value: 'OVERDUE_INVOICE', label: 'Overdue invoice' },
]

function statusColor(
  s?: string
): 'success' | 'info' | 'warning' | 'error' | 'default' {
  if (s === 'ACTIVE') return 'success'
  if (s === 'TRIAL' || s === 'PENDING') return 'info'
  if (s === 'PAST_DUE' || s === 'SUSPENDED') return 'warning'
  if (s === 'CANCELLED' || s === 'EXPIRED') return 'error'
  return 'default'
}

function resolveSubscriptionAccountType(
  sub: SubscriptionDto,
  accountById: Map<string, AccountDto>,
  planById: Map<string, SubscriptionPlanDto>,
): AccountType | '' {
  const fromAccount = accountById.get(sub.accountId)?.type
  if (fromAccount) {
    const type = String(fromAccount).toUpperCase()
    if (type === 'SHIPPER' || type === 'FREIGHTER') return type
  }
  const fromPlan = planById.get(sub.planId)?.accountType
  if (fromPlan === 'SHIPPER' || fromPlan === 'FREIGHTER') return fromPlan
  return ''
}

export default function SubscriptionsPage() {
  const [tab, setTab] = useState<TabKey>('subscriptions')
  const [page, setPage] = useState(0)
  const [overduePage, setOverduePage] = useState(0)
  const [status, setStatus] = useState<string>('')
  const [planType, setPlanType] = useState<string>('')
  const [accountTypeFilter, setAccountTypeFilter] = useState<AccountTypeFilter>('ALL')
  const [accountSearch, setAccountSearch] = useState('')
  const [search, setSearch] = useState('')

  // Subscription detail dialog
  const [selectedSubId, setSelectedSubId] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)

  // Inline action dialogs
  const [extendDays, setExtendDays] = useState('7')
  const [discountMode, setDiscountMode] = useState<'percentage' | 'amount'>('percentage')
  const [discountValue, setDiscountValue] = useState('')
  const [discountReason, setDiscountReason] = useState('')
  const [discountEndDate, setDiscountEndDate] = useState('')
  const [discountPromoCode, setDiscountPromoCode] = useState('')
  const [cancelReason, setCancelReason] = useState('')

  // Bulk reminders
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkType, setBulkType] = useState('TRIAL_ENDING')

  // Mark invoice paid
  const [markPaidInvoice, setMarkPaidInvoice] = useState<SubscriptionInvoiceDto | null>(
    null
  )
  const [markPaidMethod, setMarkPaidMethod] = useState('BANK_TRANSFER')

  // Analytics
  const today = new Date()
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
  const [revStart, setRevStart] = useState(monthAgo.toISOString().slice(0, 10))
  const [revEnd, setRevEnd] = useState(today.toISOString().slice(0, 10))
  const [churnDays, setChurnDays] = useState('30')

  // Force-cancel typed confirmation
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)

  // Row actions menu (reuses the full account-level subscription action set)
  const [actionSub, setActionSub] = useState<SubscriptionDto | null>(null)
  const [actionSubmenuAnchor, setActionSubmenuAnchor] = useState<HTMLElement | null>(
    null
  )

  const toast = useToast()
  const queryClient = useQueryClient()
  const getLocalizedLabel = useLocalizedLabel()

  // Queries
  const subscriptionsQuery = useQuery({
    queryKey: ['subscriptions', page, status, planType],
    queryFn: () =>
      subscriptionService.getAll(
        page,
        PAGE_SIZE,
        status || undefined,
        planType || undefined
      ),
    enabled: tab === 'subscriptions',
  })

  const overdueQuery = useQuery({
    queryKey: ['subscription-overdue-invoices', overduePage],
    queryFn: () => subscriptionService.getOverdueInvoices(overduePage, PAGE_SIZE),
    enabled: tab === 'overdue',
  })

  const overviewQuery = useQuery({
    queryKey: ['subscription-analytics-overview'],
    queryFn: () => analyticsService.getSubscriptionOverview(),
  })

  const revenueQuery = useQuery({
    queryKey: ['subscription-analytics-revenue', revStart, revEnd],
    queryFn: () => analyticsService.getRevenueAnalytics(revStart, revEnd),
    enabled: tab === 'analytics' && !!revStart && !!revEnd,
  })

  const churnQuery = useQuery({
    queryKey: ['subscription-analytics-churn', churnDays],
    queryFn: () => analyticsService.getChurnAnalytics(parseInt(churnDays, 10) || 30),
    enabled: tab === 'analytics',
  })

  const plansQuery = useQuery({
    queryKey: ['subscription-plans-active'],
    queryFn: () => subscriptionPlanService.getAll(false, 0, 100),
  })

  const accountsQuery = useQuery({
    queryKey: ['accounts-for-subscriptions'],
    queryFn: () => accountService.getAll(0, 500),
  })

  const selectedSubQuery = useQuery({
    queryKey: ['subscription-detail', selectedSubId],
    queryFn: () => subscriptionService.getById(selectedSubId as string),
    enabled: !!selectedSubId && detailOpen,
  })

  // Mutations
  const extendMutation = useMutation({
    mutationFn: ({ id, days }: { id: string; days: number }) =>
      subscriptionService.extendTrial(id, days),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      queryClient.invalidateQueries({ queryKey: ['subscription-detail'] })
      toast.success(`Trial extended by ${extendDays} days`)
    },
    onError: (e) => toast.errorFrom(e, 'Failed to extend trial'),
  })

  const discountMutation = useMutation({
    mutationFn: ({ id, request }: { id: string; request: any }) =>
      subscriptionService.applyDiscount(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      queryClient.invalidateQueries({ queryKey: ['subscription-detail'] })
      toast.success('Discount applied')
      setDiscountValue('')
      setDiscountReason('')
      setDiscountEndDate('')
      setDiscountPromoCode('')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to apply discount'),
  })

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      subscriptionService.forceCancel(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      queryClient.invalidateQueries({ queryKey: ['subscription-detail'] })
      toast.success('Subscription cancelled')
      setCancelReason('')
      setCancelConfirmOpen(false)
      setDetailOpen(false)
    },
    onError: (e) => toast.errorFrom(e, 'Failed to cancel subscription'),
  })

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => subscriptionService.reactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
      queryClient.invalidateQueries({ queryKey: ['subscription-detail'] })
      toast.success('Subscription reactivated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to reactivate'),
  })

  const bulkRemindersMutation = useMutation({
    mutationFn: (type: string) => subscriptionService.sendBulkReminders(type),
    onSuccess: (data: any) => {
      const count = data?.sent ?? data?.count ?? null
      toast.success(
        count != null
          ? `Reminders queued: ${count} subscription(s)`
          : 'Bulk reminders triggered'
      )
      setBulkOpen(false)
    },
    onError: (e) => toast.errorFrom(e, 'Failed to send bulk reminders'),
  })

  const markPaidMutation = useMutation({
    mutationFn: ({ invoiceId, paymentMethod }: { invoiceId: string; paymentMethod: string }) =>
      subscriptionService.markInvoicePaid(invoiceId, paymentMethod),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-overdue-invoices'] })
      toast.success('Invoice marked as paid')
      setMarkPaidInvoice(null)
    },
    onError: (e) => toast.errorFrom(e, 'Failed to mark invoice paid'),
  })

  // Derived
  const subscriptions: SubscriptionDto[] = useMemo(
    () => subscriptionsQuery.data?.content ?? [],
    [subscriptionsQuery.data]
  )
  const subscriptionsTotalPages: number = subscriptionsQuery.data?.totalPages ?? 0
  const overdueInvoices: SubscriptionInvoiceDto[] = overdueQuery.data?.content ?? []
  const overdueTotalPages: number = overdueQuery.data?.totalPages ?? 0
  const plans: SubscriptionPlanDto[] = useMemo(
    () => plansQuery.data?.content ?? plansQuery.data ?? [],
    [plansQuery.data]
  )
  const planById = useMemo(() => {
    const map = new Map<string, SubscriptionPlanDto>()
    plans.forEach((p) => map.set(p.id, p))
    return map
  }, [plans])
  const accounts: AccountDto[] = useMemo(
    () => accountsQuery.data?.content ?? [],
    [accountsQuery.data]
  )
  const accountById = useMemo(() => {
    const map = new Map<string, AccountDto>()
    accounts.forEach((a) => map.set(a.id, a))
    return map
  }, [accounts])

  const filteredSubs = useMemo(
    () =>
      subscriptions.filter((s) => {
        if (accountTypeFilter !== 'ALL') {
          const accountType = resolveSubscriptionAccountType(s, accountById, planById)
          if (accountType !== accountTypeFilter) return false
        }
        if (
          accountSearch &&
          !s.accountId.toLowerCase().includes(accountSearch.toLowerCase())
        )
          return false
        if (search) {
          const q = search.toLowerCase()
          if (
            !s.id.toLowerCase().includes(q) &&
            !(s.promotionalCode || '').toLowerCase().includes(q)
          )
            return false
        }
        return true
      }),
    [subscriptions, accountTypeFilter, accountById, planById, accountSearch, search]
  )

  const renderAccountTypeCell = (accountType: AccountType | '') => {
    if (!accountType) return '—'
    const label = getLocalizedLabel('accountType', accountType) || accountType
    const isShipper = accountType === 'SHIPPER'
    const color = isShipper ? shipperColor : freighterColor
    const Icon = isShipper ? ShipperIcon : FreighterIcon
    return (
      <Tooltip title={label}>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Avatar sx={{ bgcolor: alpha(color, 0.1), width: 28, height: 28 }}>
            <Icon sx={{ fontSize: 16, color }} />
          </Avatar>
          <Typography variant="body2">{label}</Typography>
        </Stack>
      </Tooltip>
    )
  }

  const selectedSub: SubscriptionDto | undefined = selectedSubQuery.data

  const handleExport = () => {
    if (tab === 'subscriptions') {
      exportCsv<SubscriptionDto>(
        `subscriptions-${new Date().toISOString().slice(0, 10)}`,
        [
          { header: 'ID', field: 'id' },
          { header: 'Account', field: 'accountId' },
          {
            header: 'Account Type',
            field: (r) =>
              resolveSubscriptionAccountType(r, accountById, planById) ||
              planById.get(r.planId)?.accountType ||
              accountById.get(r.accountId)?.type ||
              '',
          },
          { header: 'Plan', field: (r) => planById.get(r.planId)?.name || r.planId },
          { header: 'Status', field: 'status' },
          { header: 'Monthly Price', field: 'monthlyPrice' },
          { header: 'Currency', field: 'currency' },
          { header: 'Start', field: 'startDate' },
          { header: 'Trial End', field: 'trialEndDate' },
          { header: 'Next Billing', field: 'nextBillingDate' },
          { header: 'End', field: 'endDate' },
          { header: 'Discount %', field: 'discountPercentage' },
          { header: 'Promo Code', field: 'promotionalCode' },
        ],
        filteredSubs
      )
    } else if (tab === 'overdue') {
      exportCsv<SubscriptionInvoiceDto>(
        `overdue-invoices-${new Date().toISOString().slice(0, 10)}`,
        [
          { header: 'ID', field: 'id' },
          { header: 'Invoice #', field: 'invoiceNumber' },
          { header: 'Status', field: 'status' },
          { header: 'Total', field: 'totalAmount' },
          { header: 'Currency', field: 'currency' },
          { header: 'Due Date', field: 'dueDate' },
          { header: 'Issue Date', field: 'issueDate' },
          { header: 'Attempts', field: 'attemptCount' },
          { header: 'Failure', field: 'failureReason' },
        ],
        overdueInvoices
      )
    }
  }

  const handleRefresh = () => {
    if (tab === 'subscriptions') subscriptionsQuery.refetch()
    else if (tab === 'overdue') overdueQuery.refetch()
    else {
      overviewQuery.refetch()
      revenueQuery.refetch()
      churnQuery.refetch()
    }
  }

  // KPI tiles (from analytics/overview)
  const overview: any = useMemo(() => overviewQuery.data || {}, [overviewQuery.data])
  const kpis = useMemo(
    () => [
      {
        label: 'Active subscriptions',
        value: overview.activeSubscriptions ?? overview.active ?? '—',
        color: 'success.main',
      },
      {
        label: 'Trial',
        value: overview.trialSubscriptions ?? overview.trial ?? '—',
        color: 'info.main',
      },
      {
        label: 'Past due',
        value:
          overview.pastDueSubscriptions ??
          overview.pastDue ??
          overview.gracePeriod ??
          '—',
        color: 'warning.main',
      },
      {
        label: 'MRR',
        value:
          overview.mrr != null
            ? formatCurrency(overview.mrr)
            : overview.monthlyRecurringRevenue != null
            ? formatCurrency(overview.monthlyRecurringRevenue)
            : '—',
      },
    ],
    [overview]
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={2}
        mb={2}
      >
        <Stack direction="row" spacing={1}>
          <Tooltip title="Send a bulk reminder to subscribers">
            <Button
              size="small"
              variant="outlined"
              startIcon={<NotificationsIcon />}
              onClick={() => setBulkOpen(true)}
            >
              Bulk Reminder
            </Button>
          </Tooltip>
          {(tab === 'subscriptions' || tab === 'overdue') && (
            <Tooltip title="Export current view as CSV">
              <IconButton onClick={handleExport}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* KPI tiles */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {kpis.map((kpi) => (
          <Grid item xs={6} sm={3} key={kpi.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {kpi.label}
              </Typography>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={kpi.color ? { color: kpi.color } : undefined}
              >
                {kpi.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Tabs
        value={tab}
        onChange={(_e, v) => {
          setTab(v)
          setPage(0)
          setOverduePage(0)
        }}
        sx={{ mb: 2 }}
      >
        <Tab label="Subscriptions" value="subscriptions" />
        <Tab label="Overdue Invoices" value="overdue" />
        <Tab label="Analytics" value="analytics" />
      </Tabs>

      {/* === Subscriptions tab === */}
      {tab === 'subscriptions' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems="center"
            >
              <TextField
                select
                size="small"
                label="Account type"
                value={accountTypeFilter}
                onChange={(e) => setAccountTypeFilter(e.target.value as AccountTypeFilter)}
                sx={{ minWidth: 180 }}
                SelectProps={{
                  renderValue: (value) => {
                    if (value === 'ALL') return 'All account types'
                    const label = getLocalizedLabel('accountType', String(value)) || String(value)
                    const Icon = value === 'SHIPPER' ? ShipperIcon : FreighterIcon
                    const color = value === 'SHIPPER' ? shipperColor : freighterColor
                    return (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Icon sx={{ fontSize: 18, color }} />
                        <span>{label}</span>
                      </Stack>
                    )
                  },
                }}
              >
                <MenuItem value="ALL">All account types</MenuItem>
                <MenuItem value="SHIPPER">
                  {getLocalizedLabel('accountType', 'SHIPPER') || 'Shipper'}
                </MenuItem>
                <MenuItem value="FREIGHTER">
                  {getLocalizedLabel('accountType', 'FREIGHTER') || 'Freighter'}
                </MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Status"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value)
                  setPage(0)
                }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All statuses</MenuItem>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {getLocalizedLabel('subscriptionStatus', s) || s}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Plan"
                value={planType}
                onChange={(e) => {
                  setPlanType(e.target.value)
                  setPage(0)
                }}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">All plans</MenuItem>
                {plans.map((p) => (
                  <MenuItem key={p.id} value={p.planCode || p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Account ID"
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <TextField
                size="small"
                placeholder="Search id / promo code"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 240, flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Paper>

          {subscriptionsQuery.isLoading ? (
            <Paper sx={{ flex: 1, minHeight: 0 }}><TableSkeleton rows={8} columns={9} /></Paper>
          ) : subscriptionsQuery.isError ? (
            <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ErrorCard
                title="Failed to load subscriptions"
                message={(subscriptionsQuery.error as Error)?.message}
                onRetry={() => subscriptionsQuery.refetch()}
              />
            </Paper>
          ) : (
            <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table sx={{ height: filteredSubs.length === 0 ? '100%' : undefined }}>
                <TableHead>
                  <TableRow>
                    <ResizableTableCell>Account</ResizableTableCell>
                    <ResizableTableCell>Account Type</ResizableTableCell>
                    <ResizableTableCell>Plan</ResizableTableCell>
                    <ResizableTableCell>Status</ResizableTableCell>
                    <ResizableTableCell>Price</ResizableTableCell>
                    <ResizableTableCell>Started</ResizableTableCell>
                    <ResizableTableCell>Next Billing</ResizableTableCell>
                    <ResizableTableCell>Discount</ResizableTableCell>
                    <ResizableTableCell sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSubs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ borderBottom: 'none', verticalAlign: 'middle' }}>
                        <EmptyState
                          title="No subscriptions match the current filters"
                          description="Adjust the account type, status, plan, account, or search filters above."
                        />
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredSubs.map((sub) => {
                    const plan = planById.get(sub.planId)
                    const account = accountById.get(sub.accountId)
                    const accountType = resolveSubscriptionAccountType(sub, accountById, planById)
                    const inTrial = !!sub.trialEndDate && sub.status === 'TRIAL'
                    return (
                      <TableRow
                        key={sub.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setSelectedSubId(sub.id)
                          setDetailOpen(true)
                        }}
                      >
                        <TableCell>
                          <Tooltip title={sub.accountId}>
                            <span>{account?.businessName || `${sub.accountId.substring(0, 12)}…`}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{renderAccountTypeCell(accountType)}</TableCell>
                        <TableCell>{plan?.name || sub.planId.substring(0, 8)}</TableCell>
                        <TableCell>
                          <Chip
                            label={getLocalizedLabel('subscriptionStatus', sub.status) || sub.status}
                            color={statusColor(sub.status)}
                            size="small"
                          />
                          {inTrial && sub.trialEndDate && (
                            <Chip
                              label={`trial ends ${new Date(sub.trialEndDate).toLocaleDateString()}`}
                              size="small"
                              variant="outlined"
                              sx={{ ml: 0.5 }}
                              icon={<AccessTimeIcon />}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {sub.monthlyPrice != null
                            ? formatCurrency(sub.monthlyPrice)
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {sub.startDate
                            ? new Date(sub.startDate).toLocaleDateString()
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {sub.nextBillingDate
                            ? new Date(sub.nextBillingDate).toLocaleDateString()
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {sub.discountPercentage
                            ? `${sub.discountPercentage}%`
                            : sub.discountAmount
                            ? formatCurrency(sub.discountAmount)
                            : '—'}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()} sx={stickyActionsBodySx}>
                          <Tooltip title="View details">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedSubId(sub.id)
                                setDetailOpen(true)
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Actions">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                setActionSub(sub)
                                setActionSubmenuAnchor(e.currentTarget)
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {subscriptionsTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={subscriptionsTotalPages}
                page={page + 1}
                onChange={(_e, v) => setPage(v - 1)}
                color="primary"
              />
            </Box>
          )}
        </Box>
      )}

      {/* === Overdue invoices tab === */}
      {tab === 'overdue' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {overdueQuery.isLoading ? (
            <Paper sx={{ flex: 1, minHeight: 0 }}><TableSkeleton rows={6} columns={8} /></Paper>
          ) : overdueQuery.isError ? (
            <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ErrorCard
                title="Failed to load overdue invoices"
                message={(overdueQuery.error as Error)?.message}
                onRetry={() => overdueQuery.refetch()}
              />
            </Paper>
          ) : (
            <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table sx={{ height: overdueInvoices.length === 0 ? '100%' : undefined }}>
                <TableHead>
                  <TableRow>
                    <ResizableTableCell>Invoice #</ResizableTableCell>
                    <ResizableTableCell>Status</ResizableTableCell>
                    <ResizableTableCell>Total</ResizableTableCell>
                    <ResizableTableCell>Due Date</ResizableTableCell>
                    <ResizableTableCell>Attempts</ResizableTableCell>
                    <ResizableTableCell>Next Attempt</ResizableTableCell>
                    <ResizableTableCell>Failure Reason</ResizableTableCell>
                    <ResizableTableCell sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overdueInvoices.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ borderBottom: 'none', verticalAlign: 'middle' }}>
                        <EmptyState title="No overdue invoices" description="All invoices are current." />
                      </TableCell>
                    </TableRow>
                  )}
                  {overdueInvoices.map((inv) => {
                    const overdue = inv.dueDate
                      ? new Date(inv.dueDate).getTime() < Date.now()
                      : false
                    return (
                      <TableRow key={inv.id} hover>
                        <TableCell>
                          {inv.invoiceNumber || inv.id.substring(0, 8) + '…'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={inv.status || '—'}
                            color={overdue ? 'error' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {inv.totalAmount != null
                            ? formatCurrency(inv.totalAmount)
                            : '—'}{' '}
                          {inv.currency || ''}
                        </TableCell>
                        <TableCell>
                          {inv.dueDate
                            ? new Date(inv.dueDate).toLocaleDateString()
                            : '—'}
                        </TableCell>
                        <TableCell>{inv.attemptCount ?? '—'}</TableCell>
                        <TableCell>
                          {inv.nextPaymentAttempt
                            ? new Date(inv.nextPaymentAttempt).toLocaleString()
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={inv.failureReason || ''}>
                            <span>
                              {inv.failureReason
                                ? inv.failureReason.length > 40
                                  ? inv.failureReason.substring(0, 40) + '…'
                                  : inv.failureReason
                                : '—'}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={stickyActionsBodySx}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => {
                              setMarkPaidInvoice(inv)
                              setMarkPaidMethod('BANK_TRANSFER')
                            }}
                          >
                            Mark Paid
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {overdueTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={overdueTotalPages}
                page={overduePage + 1}
                onChange={(_e, v) => setOverduePage(v - 1)}
                color="primary"
              />
            </Box>
          )}
        </Box>
      )}

      {/* === Analytics tab === */}
      {tab === 'analytics' && (
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Revenue
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
              <TextField
                type="date"
                size="small"
                label="From"
                value={revStart}
                onChange={(e) => setRevStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                size="small"
                label="To"
                value={revEnd}
                onChange={(e) => setRevEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            {revenueQuery.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <pre
                style={{
                  margin: 0,
                  fontFamily: 'inherit',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.85rem',
                }}
              >
                {JSON.stringify(revenueQuery.data ?? {}, null, 2)}
              </pre>
            )}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Churn
            </Typography>
            <Stack direction="row" spacing={2} mb={2} alignItems="center">
              <TextField
                size="small"
                type="number"
                label="Look-back days"
                value={churnDays}
                onChange={(e) => setChurnDays(e.target.value)}
                sx={{ width: 160 }}
                inputProps={{ min: 1, max: 365 }}
              />
            </Stack>
            {churnQuery.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <pre
                style={{
                  margin: 0,
                  fontFamily: 'inherit',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.85rem',
                }}
              >
                {JSON.stringify(churnQuery.data ?? {}, null, 2)}
              </pre>
            )}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Overview
            </Typography>
            {overviewQuery.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <pre
                style={{
                  margin: 0,
                  fontFamily: 'inherit',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.85rem',
                }}
              >
                {JSON.stringify(overviewQuery.data ?? {}, null, 2)}
              </pre>
            )}
          </Paper>
        </Stack>
      )}

      {/* ===== Subscription detail dialog ===== */}
      <Dialog
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false)
          setSelectedSubId(null)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Manage Subscription</DialogTitle>
        <DialogContent dividers>
          {selectedSubQuery.isLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {selectedSub && (
            <Stack spacing={2}>
              {/* Header */}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  label={getLocalizedLabel('subscriptionStatus', selectedSub.status) || selectedSub.status}
                  color={statusColor(selectedSub.status)}
                  size="small"
                />
                {selectedSub.autoRenew === false && (
                  <Chip label="Auto-renew OFF" size="small" variant="outlined" />
                )}
                {selectedSub.cancellationType && (
                  <Chip
                    label={`Cancelled: ${selectedSub.cancellationType}`}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                )}
              </Stack>

              <PropertyView>
                <PropertySection title="Identity">
                  <PropertyField label="Subscription ID" value={selectedSub.id} mono copyable />
                  <PropertyField label="Account ID" value={selectedSub.accountId} mono copyable />
                  <PropertyField
                    label="Plan"
                    value={planById.get(selectedSub.planId)?.name || selectedSub.planId}
                  />
                  <PropertyField
                    label="Monthly Price"
                    value={
                      selectedSub.monthlyPrice != null
                        ? `${formatCurrency(selectedSub.monthlyPrice)} ${selectedSub.currency || ''}`.trim()
                        : null
                    }
                  />
                </PropertySection>
                <PropertySection title="Lifecycle" columns={3}>
                  <PropertyField label="Started" value={selectedSub.startDate} type="date" />
                  <PropertyField label="Ends" value={selectedSub.endDate} type="date" />
                  <PropertyField label="Trial Ends" value={selectedSub.trialEndDate} type="date" />
                  <PropertyField label="Next Billing" value={selectedSub.nextBillingDate} type="date" />
                  <PropertyField label="Last Billing" value={selectedSub.lastBillingDate} type="date" />
                  <PropertyField label="Grace Period End" value={selectedSub.gracePeriodEnd} type="date" />
                  <PropertyField
                    label="Current Period"
                    value={
                      selectedSub.currentPeriodStart && selectedSub.currentPeriodEnd
                        ? `${fmtDate(selectedSub.currentPeriodStart)} – ${fmtDate(selectedSub.currentPeriodEnd)}`
                        : null
                    }
                    full
                  />
                </PropertySection>
                <PropertySection title="Promotion">
                  <PropertyField
                    label="Discount"
                    value={
                      selectedSub.discountPercentage
                        ? `${selectedSub.discountPercentage}%${
                            selectedSub.discountEndDate
                              ? ` until ${fmtDate(selectedSub.discountEndDate)}`
                              : ''
                          }`
                        : selectedSub.discountAmount
                        ? formatCurrency(selectedSub.discountAmount)
                        : null
                    }
                  />
                  <PropertyField label="Promo Code" value={selectedSub.promotionalCode} />
                  <PropertyField label="Cancelled At" value={selectedSub.cancelledAt} type="datetime" full />
                </PropertySection>
              </PropertyView>

              {selectedSub.cancellationReason && (
                <Alert severity="warning" variant="outlined">
                  <strong>Cancellation reason:</strong> {selectedSub.cancellationReason}
                </Alert>
              )}

              <Divider />

              {/* Extend trial */}
              {(selectedSub.status === 'TRIAL' || selectedSub.trialEndDate) && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Extend Trial
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <TextField
                      label="Days"
                      type="number"
                      size="small"
                      value={extendDays}
                      onChange={(e) => setExtendDays(e.target.value)}
                      sx={{ width: 140 }}
                      inputProps={{ min: 1, max: 365 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AccessTimeIcon />}
                      disabled={!extendDays || extendMutation.isPending}
                      onClick={() =>
                        extendMutation.mutate({
                          id: selectedSub.id,
                          days: parseInt(extendDays, 10),
                        })
                      }
                    >
                      Extend
                    </Button>
                  </Stack>
                </Box>
              )}

              {/* Apply discount */}
              {(selectedSub.status === 'ACTIVE' || selectedSub.status === 'TRIAL') && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Apply Discount
                  </Typography>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <TextField
                        select
                        size="small"
                        label="Mode"
                        value={discountMode}
                        onChange={(e) => setDiscountMode(e.target.value as any)}
                        sx={{ minWidth: 140 }}
                      >
                        <MenuItem value="percentage">Percentage</MenuItem>
                        <MenuItem value="amount">Fixed amount</MenuItem>
                      </TextField>
                      <TextField
                        size="small"
                        type="number"
                        label={discountMode === 'percentage' ? 'Percentage' : 'Amount'}
                        value={discountValue}
                        onChange={(e) => setDiscountValue(e.target.value)}
                        sx={{ width: 160 }}
                        inputProps={
                          discountMode === 'percentage'
                            ? { min: 0, max: 100, step: 0.1 }
                            : { min: 0, max: 10000, step: 0.01 }
                        }
                      />
                      <TextField
                        size="small"
                        type="date"
                        label="Ends on"
                        value={discountEndDate}
                        onChange={(e) => setDiscountEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1.5}>
                      <TextField
                        size="small"
                        label="Promo code"
                        value={discountPromoCode}
                        onChange={(e) => setDiscountPromoCode(e.target.value)}
                        sx={{ width: 200 }}
                      />
                      <TextField
                        size="small"
                        label="Reason *"
                        value={discountReason}
                        onChange={(e) => setDiscountReason(e.target.value)}
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                    <Box>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<LocalOfferIcon />}
                        disabled={
                          !discountValue ||
                          !discountReason.trim() ||
                          discountMutation.isPending
                        }
                        onClick={() =>
                          discountMutation.mutate({
                            id: selectedSub.id,
                            request: {
                              [discountMode === 'percentage'
                                ? 'discountPercentage'
                                : 'discountAmount']: parseFloat(discountValue),
                              reason: discountReason.trim(),
                              discountEndDate: discountEndDate || undefined,
                              promotionalCode: discountPromoCode || undefined,
                            },
                          })
                        }
                      >
                        Apply Discount
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* Reactivate */}
              {(selectedSub.status === 'CANCELLED' ||
                selectedSub.status === 'EXPIRED' ||
                selectedSub.status === 'SUSPENDED') && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Reactivate Subscription
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    color="success"
                    startIcon={<RestoreIcon />}
                    disabled={reactivateMutation.isPending}
                    onClick={() => reactivateMutation.mutate(selectedSub.id)}
                  >
                    Reactivate
                  </Button>
                </Box>
              )}

              <Divider />

              {/* Force cancel */}
              {selectedSub.status !== 'CANCELLED' && selectedSub.status !== 'EXPIRED' && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom color="error.main">
                    Force Cancel Subscription
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      label="Cancellation reason *"
                      size="small"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      multiline
                      rows={2}
                      sx={{ flex: 1 }}
                      placeholder="Explain why this subscription is being force-cancelled"
                    />
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      startIcon={<CancelIcon />}
                      disabled={!cancelReason.trim() || cancelMutation.isPending}
                      onClick={() => setCancelConfirmOpen(true)}
                    >
                      Force Cancel…
                    </Button>
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDetailOpen(false)
              setSelectedSubId(null)
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Bulk reminders dialog ===== */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send Bulk Reminders</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sends the selected reminder type to every subscription that matches.
          </Typography>
          <TextField
            select
            fullWidth
            label="Reminder type"
            value={bulkType}
            onChange={(e) => setBulkType(e.target.value)}
          >
            {REMINDER_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<NotificationsIcon />}
            disabled={bulkRemindersMutation.isPending}
            onClick={() => bulkRemindersMutation.mutate(bulkType)}
          >
            {bulkRemindersMutation.isPending ? 'Sending…' : 'Send Reminders'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== Mark invoice paid dialog ===== */}
      <Dialog
        open={!!markPaidInvoice}
        onClose={() => setMarkPaidInvoice(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mark Invoice as Paid</DialogTitle>
        <DialogContent dividers>
          {markPaidInvoice && (
            <Stack spacing={2}>
              <Typography variant="body2">
                Invoice <strong>{markPaidInvoice.invoiceNumber || markPaidInvoice.id}</strong>{' '}
                · total{' '}
                <strong>
                  {markPaidInvoice.totalAmount != null
                    ? formatCurrency(markPaidInvoice.totalAmount)
                    : '—'}
                </strong>
              </Typography>
              <TextField
                select
                fullWidth
                label="Payment method *"
                value={markPaidMethod}
                onChange={(e) => setMarkPaidMethod(e.target.value)}
              >
                <MenuItem value="BANK_TRANSFER">Bank transfer</MenuItem>
                <MenuItem value="WIRE_TRANSFER">Wire transfer</MenuItem>
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="CHECK">Check</MenuItem>
                <MenuItem value="CREDIT_CARD">Credit card</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidInvoice(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            disabled={!markPaidMethod || markPaidMutation.isPending}
            onClick={() =>
              markPaidInvoice &&
              markPaidMutation.mutate({
                invoiceId: markPaidInvoice.id,
                paymentMethod: markPaidMethod,
              })
            }
          >
            Mark Paid
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force-cancel typed confirmation */}
      <ConfirmDialog
        open={cancelConfirmOpen}
        title="Force-cancel subscription?"
        description={
          <>
            This immediately cancels the subscription. Billing stops and the account loses
            plan access. This cannot be undone.
            {cancelReason ? (
              <>
                {' '}Reason: <strong>{cancelReason}</strong>
              </>
            ) : null}
          </>
        }
        confirmPhrase="CANCEL"
        confirmHelper="Type CANCEL to confirm."
        confirmLabel="Force Cancel"
        severity="error"
        loading={cancelMutation.isPending}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() =>
          selectedSub &&
          cancelMutation.mutate({ id: selectedSub.id, reason: cancelReason.trim() })
        }
      />

      {/* Row actions — full subscription action set (change plan, discount, free period,
          billing options, plan bounce, cancel, reactivate). Reuses the account-level component. */}
      <AccountSubscriptionActions
        accountId={actionSub?.accountId ?? null}
        accountType={
          actionSub ? accountById.get(actionSub.accountId)?.type ?? '' : ''
        }
        subscriptions={actionSub ? [actionSub] : []}
        plans={plans}
        submenuAnchor={actionSubmenuAnchor}
        parentMenuOpen={Boolean(actionSubmenuAnchor)}
        onSubmenuClose={() => setActionSubmenuAnchor(null)}
        onCloseAll={() => {
          setActionSubmenuAnchor(null)
          setActionSub(null)
        }}
      />
    </Box>
  )
}
